import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { notification, Spin } from "antd";
import GoogleAddressSelect from "../../experience/carecoordinator/patient/GoogleAddressSelect";
import "./squareCardForm.css";
import IntentType from "../../intentexecutor/utils/IntentType";
import { performPostRequest } from "../../service/network/NetworkService";
import { getApiUrl } from "../../service/network/UrlMapper";
import { useGetOrgIdentifier } from "../../experience/generic/hooks/useGetOrgIdentifier";

const SQUARE_SDK_URL = "https://web.squarecdn.com/v1/square.js";
/** Delay (ms) before attaching Square card — modal body must be visible and laid out or iframe may not render. */
const CARD_ATTACH_DELAY_MS = 150;

/** Map country name from Google to 2-letter code for Square billingContact */
function toCountryCode(countryName) {
  if (!countryName) return "US";
  const n = countryName.trim().toLowerCase();
  if (n === "united states" || n === "united states of america" || n === "usa")
    return "US";
  if (n === "united kingdom" || n === "uk") return "GB";
  if (n === "canada") return "CA";
  return countryName.length === 2 ? countryName.toUpperCase() : "US";
}

/**
 * Loads the Square Web Payments SDK script (sandbox).
 */
function loadSquareScript() {
  if (typeof window !== "undefined" && window.Square) {
    return Promise.resolve();
  }
  const existing = document.querySelector(
    `script[src="${SQUARE_SDK_URL}"]`,
  );
  if (existing) {
    return new Promise((resolve) => {
      if (window.Square) return resolve();
      existing.addEventListener("load", () => resolve());
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = SQUARE_SDK_URL;
    script.async = true;
    script.onload = () => {
      if (window.Square) resolve();
      else reject(new Error("Square SDK failed to load properly."));
    };
    script.onerror = () => reject(new Error("Failed to load Square SDK."));
    document.body.appendChild(script);
  });
}

/**
 * SquareCardForm – Save card (autopay). Square SDK for card fields; existing Google Address for billing.
 * Submit is triggered from the parent (e.g. modal footer PrimaryButton) via ref.submitCard().
 */
const SquareCardForm = forwardRef(function SquareCardForm(
  { onCardTokenized, onSubmitStateChange },
  ref,
) {
  const cardInstanceRef = useRef(null);
  const paymentsRef = useRef(null);
  const cardContainerRef = useRef(null);
  const attachTimerRef = useRef(null);

  const organizationIdentifier = useGetOrgIdentifier();
  const [squareConfig, setSquareConfig] = useState(null);
  /** True until configuration fetch finishes (success or failure). */
  const [isConfigLoading, setConfigLoading] = useState(true);
  const [isSdkReady, setSdkReady] = useState(false);
  const [isProcessing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isInitializationError, setInitializationError] = useState(false);

  const [cardholderName, setCardholderName] = useState("");
  const [addressData, setAddressData] = useState({
    completeAddress: "",
    addressLocality: "",
    addressRegion: "",
    streetAddress: "",
    postalCode: "",
    addressCountry: "",
  });
  const [addressErrors, setAddressErrors] = useState({});

  const reportError = useCallback((message) => {
    setErrorMessage(message);
  }, []);

  const clearError = useCallback(() => setErrorMessage(""), []);

  const canSubmit =
    isSdkReady && !isInitializationError && !isConfigLoading;
  const isCardLoading =
    !isConfigLoading && !isSdkReady && !isInitializationError;
  const showFormOverlay = isConfigLoading || isCardLoading;

  useEffect(() => {
    if (typeof onSubmitStateChange === "function") {
      onSubmitStateChange({ isProcessing, canSubmit });
    }
  }, [isProcessing, canSubmit, onSubmitStateChange]);

  useEffect(() => {
    if (isConfigLoading) {
      return undefined;
    }

    let cancelled = false;
    const appId = squareConfig?.squareAppId;
    const locationId = squareConfig?.squareLocationId;

    if (!appId || !locationId) {
      setInitializationError(true);
      reportError(
        "Square is not configured. Missing Application ID or Location ID.",
      );
      return undefined;
    }

    const attachCardToElement = (card) => {
      const el = cardContainerRef.current;
      if (!el || cancelled) {
        return Promise.reject(new Error("Card container is not ready."));
      }
      // Square accepts HTMLElement — avoids duplicate #id issues and targets this instance only.
      return card.attach(el);
    };

    const scheduleAttach = (card) => {
      return new Promise((resolve, reject) => {
        if (attachTimerRef.current) clearTimeout(attachTimerRef.current);
        attachTimerRef.current = setTimeout(() => {
          attachTimerRef.current = null;
          const tryAttach = async () => {
            // Modal may mount children late; retry until container exists or timeout.
            const maxAttempts = 40;
            const intervalMs = 50;
            for (let attempt = 0; attempt < maxAttempts; attempt++) {
              if (cancelled) throw new Error("Aborted");
              if (cardContainerRef.current) break;
              await new Promise((r) => setTimeout(r, intervalMs));
            }
            await attachCardToElement(card);
            if (typeof card.recalculateSize === "function") {
              try {
                card.recalculateSize();
              } catch (ignored) {}
            }
          };
          tryAttach().then(resolve).catch(reject);
        }, CARD_ATTACH_DELAY_MS);
      });
    };

    loadSquareScript()
      .then(() => {
        if (cancelled) return;
        if (!window.Square)
          throw new Error("Square.js failed to load properly.");
        const payments = window.Square.payments(appId, locationId);
        paymentsRef.current = payments;
        return payments.card();
      })
      .then((card) => {
        if (cancelled) return;
        cardInstanceRef.current = card;
        return scheduleAttach(card);
      })
      .then(() => {
        if (cancelled) return;
        setSdkReady(true);
        setInitializationError(false);
        clearError();
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err?.message ||
          "Unable to load payment form. Please try again later.";
        setInitializationError(true);
        reportError(message);
      });

    return () => {
      cancelled = true;
      if (attachTimerRef.current) {
        clearTimeout(attachTimerRef.current);
        attachTimerRef.current = null;
      }
      const card = cardInstanceRef.current;
      if (card && typeof card.destroy === "function") {
        card.destroy().catch(() => {});
        cardInstanceRef.current = null;
      }
      paymentsRef.current = null;
    };
  }, [reportError, clearError, squareConfig, isConfigLoading]);

  const validateBillingFields = useCallback(() => {
    const name = cardholderName.trim();
    if (!name) return "Please enter the cardholder name.";
    if (!addressData.completeAddress || !addressData.completeAddress.trim()) {
      setAddressErrors((prev) => ({
        ...prev,
        completeAddress: "Please select a billing address.",
      }));
      return "Please select a billing address.";
    }
    setAddressErrors({});
    return null;
  }, [cardholderName, addressData.completeAddress]);

  const submitCard = useCallback(async () => {
    if (
      isProcessing ||
      isConfigLoading ||
      isInitializationError ||
      !isSdkReady ||
      !cardInstanceRef.current
    ) {
      return;
    }

    clearError();
    setProcessing(true);

    const validationError = validateBillingFields();
    if (validationError) {
      reportError(validationError);
      setProcessing(false);
      return;
    }

    try {
      const verificationDetails = {
        intent: "STORE",
        customerInitiated: true,
        sellerKeyedIn: false,
        billingContact: {
          givenName: cardholderName.trim(),
          familyName: "",
          addressLines: [
            addressData.streetAddress || addressData.completeAddress,
          ].filter(Boolean),
          city: addressData.addressLocality || "",
          state: addressData.addressRegion || "",
          countryCode: toCountryCode(addressData.addressCountry),
          postalCode: addressData.postalCode || "",
        },
      };

      const tokenResult =
        await cardInstanceRef.current.tokenize(verificationDetails);

      if (tokenResult.status === "OK" && tokenResult.token) {
        setErrorMessage("");
        onCardTokenized(tokenResult.token);
      } else {
        const errors = tokenResult.errors || [];
        const message =
          errors.length > 0
            ? errors.map((err) => err.message || err.code).join(". ")
            : "Card validation failed. Please check your details and try again.";
        reportError(message);
        notification.error({
          message: "Please check your details and try again.",
          placement: "bottomRight",
        });
      }
    } catch (err) {
      const message = err?.message || "Something went wrong. Please try again.";
      reportError(message);
      notification.error({
        message,
        placement: "bottomRight",
      });
    } finally {
      setProcessing(false);
    }
  }, [
    isProcessing,
    isConfigLoading,
    isInitializationError,
    isSdkReady,
    clearError,
    validateBillingFields,
    reportError,
    cardholderName,
    addressData.streetAddress,
    addressData.completeAddress,
    addressData.addressLocality,
    addressData.addressRegion,
    addressData.addressCountry,
    addressData.postalCode,
    onCardTokenized,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      submitCard,
    }),
    [submitCard],
  );

  const handleFormSubmit = (e) => {
    e.preventDefault();
    submitCard();
  };

  useEffect(() => {
    let cancelled = false;

    const fail = (message) => {
      if (cancelled) return;
      setSquareConfig(null);
      setSdkReady(false);
      setInitializationError(true);
      reportError(message);
      setConfigLoading(false);
    };

    const loadConfiguration = async () => {
      setConfigLoading(true);
      setSdkReady(false);
      setInitializationError(false);
      clearError();
      setSquareConfig(null);

      const payload = {
        intent: "get_configuration",
        payload: {
          configurationKey: ["niramaya.feature.billing.square.profile"],
        },
        context: {
          organizationIdentifier,
        },
      };

      try {
        const result = await performPostRequest(
          getApiUrl()[IntentType.GET_CONFIGURATION],
          JSON.stringify(payload),
        );

        if (cancelled) return;

        if (!(result instanceof Response)) {
          fail("Unable to reach payment settings. Check your connection.");
          return;
        }

        if (!result.ok) {
          fail("Could not load payment settings. Please try again later.");
          return;
        }

        let body;
        try {
          body = await result.json();
        } catch {
          fail("Invalid response from server. Please try again later.");
          return;
        }

        if (cancelled) return;

        const row = Array.isArray(body) ? body[0] : body;
        const raw = row?.configurationValue;
        let parsed = {};
        if (raw != null && String(raw).trim() !== "") {
          try {
            parsed = JSON.parse(String(raw));
          } catch {
            fail("Payment settings are misconfigured. Contact support.");
            return;
          }
        }

        if (cancelled) return;
        setSquareConfig(parsed);
        setInitializationError(false);
        setConfigLoading(false);
      } catch {
        if (!cancelled) {
          fail("Could not load payment settings. Please try again later.");
        }
      }
    };

    loadConfiguration();
    return () => {
      cancelled = true;
    };
  }, [organizationIdentifier, reportError, clearError]);

  return (
    <form
      id="square-card-form"
      onSubmit={handleFormSubmit}
      className={`square-card-form${showFormOverlay ? " square-card-form--busy" : ""}`}
      noValidate
    >
      {showFormOverlay && (
        <div
          className="square-card-form-loader"
          aria-busy="true"
          aria-live="polite"
        >
          <Spin
            size="large"
            tip={
              isConfigLoading
                ? "Loading payment settings…"
                : "Loading secure card form…"
            }
          />
        </div>
      )}
      <div className="square-card-form-section">
        <label
          htmlFor="square-cardholder-name"
          className="square-card-form-label"
        >
          Cardholder Name
        </label>
        <input
          id="square-cardholder-name"
          type="text"
          className="square-card-form-input"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder="Name on card"
          autoComplete="cc-name"
          disabled={isProcessing || showFormOverlay}
        />
      </div>

      <div className="square-card-form-section">
        <GoogleAddressSelect
          address={addressData.completeAddress}
          setData={setAddressData}
          errors={addressErrors}
          required
          label="Billing address"
          readOnly={isProcessing || showFormOverlay}
        />
      </div>

      <div>
        <span className="square-card-form-label">Card details</span>
        <div className="square-card-container-wrap">
          <div
            ref={cardContainerRef}
            className="square-card-container"
            aria-label="Card number, expiry, CVV, and ZIP"
            aria-hidden={showFormOverlay}
          />
        </div>
      </div>

      {errorMessage && (
        <div className="square-card-form-error" role="alert">
          {errorMessage}
        </div>
      )}
    </form>
  );
});

SquareCardForm.displayName = "SquareCardForm";

export default SquareCardForm;
